// frontend/src/pages/ResetPassword.js

import React, { memo, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check } from "phosphor-react";

import { api } from "../api";
import PreLoginHeader from "../components/PreLoginHeader";

const OceanSparkleBackground = memo(function OceanSparkleBackground() {
  return (
    <div aria-hidden="true" className="ocean-background">
      <div className="ocean-light" />

      <div className="ocean-wave ocean-wave-one" />
      <div className="ocean-wave ocean-wave-two" />
      <div className="ocean-wave ocean-wave-three" />

      <div className="ocean-clouds">
        <div className="ocean-cloud ocean-cloud-one" />
        <div className="ocean-cloud ocean-cloud-two" />
        <div className="ocean-cloud ocean-cloud-three" />
        <div className="ocean-cloud ocean-cloud-four" />
        <div className="ocean-cloud ocean-cloud-five" />
      </div>

      <div className="ocean-wash" />
    </div>
  );
});

const DesktopBackgroundText = memo(function DesktopBackgroundText() {
  return (
    <div
      aria-hidden="true"
      className="
        pointer-events-none absolute inset-0 z-[2]
        hidden select-none overflow-hidden lg:block
      "
    >
      <p
        className="
          absolute -left-2 top-10 max-w-[390px]
          text-[78px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-50
          xl:text-[92px]
        "
      >
        where
        <br />
        every day
        <br />
        is a win
      </p>

      <p
        className="
          absolute right-[9%] top-[54%] max-w-[500px]
          text-[68px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        find your
        <br />
        community
      </p>

      <p
        className="
          absolute right-0 top-[30%] max-w-[590px]
          text-right text-[68px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        do more of
        <br />
        what you love
      </p>

      <p
        className="
          absolute bottom-[8%] right-0 max-w-[530px]
          text-right text-[66px] font-black leading-[0.8]
          tracking-[-0.07em] text-blue-100
          xl:text-[64px]
        "
      >
        celebrate your
        <br />
        every step
      </p>
    </div>
  );
});

export default function ResetPassword() {
  const [searchParams] = useSearchParams();

  const token = useMemo(
    () => searchParams.get("token") || "",
    [searchParams]
  );

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const canSubmit =
    Boolean(password.trim()) &&
    Boolean(confirm.trim()) &&
    !loading;

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setMsg("");

    if (!token) {
      setError("Reset link is invalid or expired.");
      return;
    }

    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post("/auth/password/reset", {
        token,
        new_password: password,
      });

      setSuccess(true);
      setMsg(data?.message || "Your password has been reset.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Network or server error";

      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 text-[#181411]">
      <section className="relative min-h-screen overflow-hidden">
        <OceanSparkleBackground />
        <PreLoginHeader />
        <DesktopBackgroundText />

        <div
          className="
            relative z-10 flex min-h-screen
            items-center justify-center
            px-6 pb-14 sm:pb-10 pt-20 sm:pt-24
          "
        >
          <main className="flex w-full max-w-[400px] flex-col items-center">
            <h1
              className="
                logo-font mb-8 select-none
                text-[60px] font-black leading-[0.9]
                tracking-[-0.06em] text-blue-500
                drop-shadow-[0_2px_14px_rgba(255,255,255,0.85)]
                sm:text-[72px]
              "
            >
              third place
            </h1>

            <div
              className="
                w-full max-w-[390px] -mt-1
                rounded-[32px] bg-slate-50/80
                px-4 py-5
                shadow-[0_24px_70px_rgba(22,59,86,0.04)]
                sm:p-5
              "
            >
              <form className="space-y-2" onSubmit={handleSubmit}>
                <div className="px-3 text-start">
                  <h2 className="text-lg font-black tracking-[-0.02em] text-[#181411]">
                    Choose a new password
                  </h2>
                </div>

                {error && (
                  <p
                    className="
                      -mt-1 rounded-2xl
                      px-4 pb-1 text-left
                      text-sm text-red-600
                    "
                    aria-live="polite"
                  >
                    {error}
                  </p>
                )}

                {msg && !success && (
                  <p
                    className="
                      rounded-2xl bg-blue-50/80
                      px-4 py-3 text-left
                      text-sm text-[#6f665d]
                    "
                    aria-live="polite"
                  >
                    {msg}
                  </p>
                )}

                {!success ? (
                  <>
                    <div>
                      <label
                        htmlFor="reset-password"
                        className="
                          mb-1.5 ml-3 block
                          text-sm font-semibold text-[#6f665d]
                        "
                      >
                        New password
                      </label>

                      <input
                        id="reset-password"
                        type={showPasswords ? "text" : "password"}
                        required
                        value={password}
                        onChange={(event) =>
                          setPassword(event.target.value)
                        }
                        autoComplete="new-password"
                        placeholder="••••••••"
                        className="
                          w-full rounded-3xl
                          border border-white/80 bg-blue-50/85
                          !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                          placeholder:text-slate-400
                          focus:border-blue-500 focus:border-2
                        "
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-reset-password"
                        className="
                          mb-1.5 ml-3 block
                          text-sm font-semibold text-[#6f665d]
                        "
                      >
                        Confirm new password
                      </label>

                      <input
                        id="confirm-reset-password"
                        type={showPasswords ? "text" : "password"}
                        required
                        value={confirm}
                        onChange={(event) =>
                          setConfirm(event.target.value)
                        }
                        autoComplete="new-password"
                        placeholder="••••••••"
                        className="
                          w-full rounded-3xl
                          border border-white/80 bg-blue-50/85
                          !min-h-0 !min-w-0 !px-3 !py-1.5 text-sm outline-none
                          placeholder:text-slate-400
                          focus:border-blue-500 focus:border-2
                        "
                      />
                    </div>

                    <label
                      className="
                        inline-flex cursor-pointer
                        items-center px-3
                        text-sm text-[#6f665d]
                      "
                    >
                      <input
                        type="checkbox"
                        checked={showPasswords}
                        onChange={() =>
                          setShowPasswords((previousValue) => !previousValue)
                        }
                        className="peer sr-only"
                      />

                      <div
                        className={`
                          flex h-4 w-4 shrink-0
                          items-center justify-center
                          rounded-full border
                          transition-colors
                          ${
                            showPasswords
                              ? "border-blue-500 bg-blue-500"
                              : "border-slate-200 bg-blue-50"
                          }
                        `}
                      >
                        <Check
                          size={12}
                          weight="bold"
                          className={`
                            text-white transition-opacity
                            ${
                              showPasswords
                                ? "opacity-100"
                                : "opacity-0"
                            }
                          `}
                        />
                      </div>

                      <span className="ml-2">Show passwords</span>
                    </label>

                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="
                        w-full transform-gpu rounded-full
                        bg-blue-600 !min-h-0 !min-w-0 !py-3
                        text-sm font-semibold text-white
                        transition-all duration-300 ease-out
                        hover:scale-[1.03]
                        hover:bg-blue-700
                        active:scale-[0.99]
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                        disabled:hover:scale-100
                        disabled:hover:bg-blue-600
                      "
                    >
                      {loading ? "resetting…" : "Reset password"}
                    </button>

                    <div className="text-center text-xs !mt-4 text-[#8a8078]">
                      <button
                        type="button"
                        onClick={() => navigate("/login")}
                        className="
                          !min-h-0 !min-w-0
                          font-semibold text-blue-400
                          transition hover:font-bold
                        "
                      >
                        Back to sign in
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate("/signup")}
                        className="
                          ml-3 !min-h-0 !min-w-0
                          font-medium text-blue-400
                          transition hover:font-bold
                        "
                      >
                        Sign up
                      </button>
                    </div>
                  </>
                ) : (
                  <div
                    className="
                      rounded-[24px] bg-blue-50/80
                      px-5 py-5 text-center
                    "
                    aria-live="polite"
                  >
                    <p className="text-sm font-semibold text-[#6f665d]">
                      {msg || "Your password has been reset."}
                    </p>

                    <button
                      type="button"
                      onClick={() => navigate("/login")}
                      className="
                        mt-4 w-full transform-gpu
                        rounded-full bg-blue-600 py-3
                        text-sm font-semibold text-white
                        transition-all duration-300 ease-out
                        hover:scale-[1.03]
                        hover:bg-blue-700
                        active:scale-[0.99]
                      "
                    >
                      Back to sign in
                    </button>
                  </div>
                )}
              </form>
            </div>
          </main>
        </div>
      </section>
    </div>
  );
}
